'use client'

import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { useWithdrawal } from '@/hooks/useWithdrawal'
import { CHAIN_CONFIG, getExplorerUrl } from '@/lib/api/relay'
import { useTrading } from '@/providers/TradingProvider'
import { useModalStore } from '@/stores/modal-store'
import {
	getAddressFormatHint,
	isValidAddressForChain,
	truncateAddress,
} from '@/utils/address-validation'
import { AlertCircle, ArrowDown, CheckCircle2, ExternalLink, Loader2 } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

export function WithdrawModal() {
	const { activeModal, closeModal } = useModalStore()
	const isOpen = activeModal === 'withdraw'

	// Get trading session for RelayClient (gas sponsorship)
	const { relayClient, isReady } = useTrading()

	const {
		polygonBalance,
		solanaBalance,
		totalBalance,
		quote,
		fetchQuote,
		execute,
		status,
		currentStepIndex,
		error,
		txHashes,
		reset,
	} = useWithdrawal()

	// Form state
	const [destinationChainId, setDestinationChainId] = useState<number>(CHAIN_CONFIG.polygon.id)
	const [recipientAddress, setRecipientAddress] = useState('')
	const [amount, setAmount] = useState('')
	const [isMaxAmount, setIsMaxAmount] = useState(false)

	// Validation
	const isValidAddress = recipientAddress
		? isValidAddressForChain(recipientAddress, destinationChainId)
		: true
	const amountNum = Number.parseFloat(amount) || 0
	// When max is selected, skip the balance check (raw balances will be used)
	const isValidAmount = amountNum > 0 && (isMaxAmount || amountNum <= totalBalance)

	const canSubmit = isValidAddress && isValidAmount && recipientAddress.length > 0

	// Reset form when modal closes
	useEffect(() => {
		if (!isOpen) {
			setRecipientAddress('')
			setAmount('')
			setIsMaxAmount(false)
			reset()
		}
	}, [isOpen, reset])

	// Handle max button
	const handleMax = useCallback(() => {
		setAmount(totalBalance.toFixed(2))
		setIsMaxAmount(true)
	}, [totalBalance])

	// Handle get quote
	const handleGetQuote = useCallback(async () => {
		if (!canSubmit) {
			return
		}
		await fetchQuote({
			amount,
			destinationChainId,
			recipientAddress,
			useMaxBalance: isMaxAmount,
		})
	}, [amount, destinationChainId, recipientAddress, canSubmit, fetchQuote, isMaxAmount])

	// Handle confirm - trading session auto-initializes on wallet connect
	const handleConfirm = useCallback(async () => {
		// If we need Polygon and session isn't ready, wait for auto-init
		const needsPolygon = quote?.steps.some(step =>
			step.items.some(item => item.data?.chainId === CHAIN_CONFIG.polygon.id)
		)

		if (needsPolygon && !isReady) {
			// Session auto-initializes - wait for it
			return
		}

		execute(relayClient)
	}, [execute, relayClient, quote, isReady])

	// Handle close
	const handleClose = useCallback(() => {
		if (status === 'executing') {
			return
		} // Prevent closing during execution
		closeModal()
	}, [status, closeModal])

	return (
		<Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
			<DialogContent
				closeButtonContainerClasses="top-[21px] size-[26px]"
				className="gap-[24px] rounded-3xl border border-white/10 bg-brand-dark-light p-[24px] sm:max-w-[480px]"
			>
				<DialogHeader>
					<DialogTitle className="text-center text-[18px] text-white leading-[100%]">
						Withdraw USDC
					</DialogTitle>
					<DialogDescription className="text-center text-brand-gray-secondary leading-[100%]">
						Transfer USDC to an external wallet
					</DialogDescription>
				</DialogHeader>

				{/* Form View */}
				{(status === 'idle' || status === 'loading_quote' || status === 'error') && (
					<div className="flex flex-col gap-[24px]">
						<div className="flex flex-col gap-[12px]">
							<div className="flex flex-col gap-[12px] rounded-[16px] border border-brand-gray bg-brand-dark p-[20px]">
								<div className="flex items-center justify-between">
									<p className=" text-[16px] text-white/70 leading-[100%]">Available Balance</p>
									<p className=" text-[16px] text-white leading-[100%]">
										{destinationChainId === CHAIN_CONFIG.solana.id
											? `${solanaBalance.toFixed(2)} USDC`
											: `${polygonBalance.toFixed(2)} USDC`}
									</p>
								</div>
								<div className="flex items-center justify-between gap-[12px]">
									<div className="flex items-center gap-[8px] overflow-hidden">
										<input
											type="text"
											inputMode="decimal"
											value={amount}
											onChange={e => {
												const value = e.target.value
												if (value === '' || /^\d*\.?\d*$/.test(value)) {
													setAmount(value)
													setIsMaxAmount(false)
												}
											}}
											placeholder="0"
											className="field-sizing-content min-w-0 flex-1 bg-transparent text-[48px] text-white leading-[100%] [appearance:textfield] placeholder:text-white/30 focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
										/>
										<span className="shrink-0 text-[24px] text-white/50 leading-[100%]">USDC</span>
									</div>
									<button
										type="button"
										onClick={handleMax}
										className="shrink-0 rounded-[12px] bg-white/5 px-[16px] py-[10px] text-[14px] text-white leading-[100%] transition-colors hover:bg-white/20"
									>
										MAX
									</button>
								</div>
								{!isMaxAmount &&
									amountNum >
										(destinationChainId === CHAIN_CONFIG.solana.id
											? solanaBalance
											: polygonBalance) && (
										<p className=" text-red-500 text-xs">Insufficient balance</p>
									)}
							</div>
						</div>

						{/* Recipient Address */}
						<div className="flex flex-col gap-[8px]">
							<p className=" text-[16px] text-white/70 leading-[100%]">Recipient Address</p>
							<input
								type="text"
								value={recipientAddress}
								onChange={e => setRecipientAddress(e.target.value)}
								placeholder={getAddressFormatHint(destinationChainId)}
								className={`w-full rounded-[8px] border bg-brand-dark px-[12px] py-[15px] text-[14px] text-white leading-[100%] placeholder:text-white/75 focus:outline-none focus:ring-1 ${
									recipientAddress && !isValidAddress
										? 'border-red-500 focus:ring-red-500'
										: 'border-brand-gray focus:ring-brand-blue'
								}`}
							/>
							{recipientAddress && !isValidAddress && (
								<p className="mt-[2px] text-[16px] text-white/70 leading-[100%]">
									Invalid address format
								</p>
							)}
						</div>

						{/* Destination Chain */}
						<div className="flex flex-col gap-[8px]">
							<p className=" text-[16px] text-white/70 leading-[100%]">Destination Chain</p>
							<Select
								value={destinationChainId.toString()}
								onValueChange={val => {
									setDestinationChainId(Number.parseInt(val))
									setRecipientAddress('') // Clear address when chain changes
								}}
							>
								<SelectTrigger className="h-[48px]! w-full rounded-[8px]! border-brand-gray! bg-brand-dark-light! p-[12px] text-[14px] text-white leading-[100%]">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={CHAIN_CONFIG.polygon.id.toString()}>
										<div className="flex items-center gap-2">
											<img
												src="/logos/matic-logo.png"
												alt="Polygon"
												width={24}
												height={24}
												className="rounded-full"
											/>
											Polygon (EVM)
										</div>
									</SelectItem>
									<SelectItem value={CHAIN_CONFIG.solana.id.toString()}>
										<div className="flex items-center gap-2">
											<img
												src="/logos/solana.png"
												alt="Solana"
												width={24}
												height={24}
												className="rounded-full"
											/>
											Solana
										</div>
									</SelectItem>
								</SelectContent>
							</Select>
						</div>

						{/* Error */}
						{error && (
							<div className="flex items-center gap-2 rounded-xl border border-red-500/20 bg-red-500/10 p-3 text-[16px] text-red-400 leading-[100%]">
								<AlertCircle className="h-4 w-4 shrink-0" />
								{error}
							</div>
						)}

						{/* Submit Button */}
						<Button
							onClick={handleGetQuote}
							disabled={!canSubmit || status === 'loading_quote'}
							className="h-12 w-full rounded-[12px] bg-brand-blue font-medium text-white hover:bg-brand-blue/90 disabled:opacity-50"
						>
							{status === 'loading_quote' ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Getting Quote...
								</>
							) : (
								'Continue'
							)}
						</Button>
					</div>
				)}

				{/* Quote Confirmation View */}
				{status === 'quote_ready' && quote && (
					<div className="flex flex-col gap-[24px]">
						<div className="rounded-[16px] border border-brand-gray bg-brand-dark p-[20px]">
							<div className="mb-[12px] flex items-center justify-between">
								<span className=" text-[16px] text-white/70 leading-[100%]">You send</span>
								<span className="font-bold text-[24px] text-white leading-[100%]">
									${quote.inputAmount}
								</span>
							</div>
							<div className="flex items-center justify-center py-[8px]">
								<ArrowDown className="h-5 w-5 text-white/70" />
							</div>
							<div className="flex items-center justify-between">
								<span className=" text-[16px] text-white/70 leading-[100%]">They receive</span>
								<span className="font-bold text-[24px] text-white leading-[100%]">
									${quote.estimatedOutput}
								</span>
							</div>
						</div>

						<div className="flex flex-col gap-[8px] rounded-[16px] border border-brand-gray bg-brand-dark p-[20px]">
							<div className="flex justify-between">
								<span className=" text-[16px] text-white/70 leading-[100%]">Destination</span>
								<span className="flex items-center gap-1.5 text-[16px] text-white leading-[100%]">
									<span
										className={`h-2 w-2 rounded-full ${
											destinationChainId === CHAIN_CONFIG.solana.id
												? 'bg-green-500'
												: 'bg-purple-500'
										}`}
									/>
									{destinationChainId === CHAIN_CONFIG.solana.id ? 'Solana' : 'Polygon'}
								</span>
							</div>
							<div className="flex justify-between">
								<span className=" text-[16px] text-white/70 leading-[100%]">Recipient</span>
								<span className=" font-mono text-[16px] text-white leading-[100%]">
									{truncateAddress(recipientAddress, 6)}
								</span>
							</div>
							<div className="flex justify-between">
								<span className=" text-[16px] text-white/70 leading-[100%]">Network Fee</span>
								<span className=" text-[16px] text-emerald-400 leading-[100%]">Sponsored</span>
							</div>
							<div className="flex justify-between">
								<span className=" text-[16px] text-white/70 leading-[100%]">Relay Fee</span>
								<span className=" text-[16px] text-white leading-[100%]">${quote.totalFeeUsd}</span>
							</div>
							<div className="flex justify-between">
								<span className=" text-[16px] text-white/70 leading-[100%]">Est. Time</span>
								<span className=" text-[16px] text-white leading-[100%]">
									~{quote.timeEstimate}s
								</span>
							</div>
						</div>

						<div className="flex gap-[12px]">
							<Button
								variant="outline"
								onClick={reset}
								className="h-12 flex-1 rounded-[12px] border-white/10 text-white hover:bg-white/5"
							>
								Back
							</Button>
							<Button
								onClick={handleConfirm}
								className="h-12 flex-1 rounded-[12px] bg-brand-blue font-medium text-white hover:bg-brand-blue/90"
							>
								Confirm Withdrawal
							</Button>
						</div>
					</div>
				)}

				{/* Executing View */}
				{status === 'executing' && quote && (
					<div className="flex flex-col items-center gap-[16px] py-[32px]">
						<Loader2 className="h-12 w-12 animate-spin text-brand-blue" />
						<div className="text-center">
							<p className=" font-medium text-[18px] text-white leading-[100%]">
								Processing Withdrawal
							</p>
							<p className="mt-[8px] text-[16px] text-white/70 leading-[100%]">
								Step {currentStepIndex + 1} of {quote.steps.length}
							</p>
						</div>
						<p className="text-center text-[14px] text-white/70 leading-[100%]">
							Please confirm any wallet prompts...
						</p>
					</div>
				)}

				{/* Success View */}
				{status === 'success' && (
					<div className="flex flex-col items-center gap-[16px] py-[32px]">
						<CheckCircle2 className="h-12 w-12 text-emerald-500" />
						<div className="text-center">
							<p className=" font-medium text-[18px] text-white leading-[100%]">
								Withdrawal Complete!
							</p>
							<p className="mt-[8px] text-[16px] text-white/70 leading-[100%]">
								${quote?.estimatedOutput} sent to {truncateAddress(recipientAddress, 6)}
							</p>
						</div>
						{txHashes.length > 0 && (
							<div className="flex flex-wrap justify-center gap-[8px]">
								{txHashes.map(tx => (
									<a
										key={tx.hash}
										href={getExplorerUrl(tx.chainId, tx.hash)}
										target="_blank"
										rel="noopener noreferrer"
										className="flex items-center gap-1 rounded-[8px] bg-white/5 px-[12px] py-[8px] text-[14px] text-brand-blue leading-[100%] hover:bg-white/10"
									>
										View on Explorer <ExternalLink className="h-3 w-3" />
									</a>
								))}
							</div>
						)}
						<Button
							onClick={handleClose}
							className="mt-[8px] h-12 w-full rounded-[12px] bg-brand-blue font-medium text-white hover:bg-brand-blue/90"
						>
							Done
						</Button>
					</div>
				)}
			</DialogContent>
		</Dialog>
	)
}
